import { useChapterHeadings } from "../composables/useChapterHeadings";
import { useSceneOpeningsStore } from "./useSceneOpeningsStore";
import { useOutlineEntitiesStore } from "./useOutlineEntitiesStore";
import { noteEntryDisplayName } from "../lib/tauri/notes";
import { unplacedItemDisplayName } from "../lib/tauri/unplaced";
import { sceneCustomName, sceneOpeningText } from "../lib/sceneLabel";
import type { ChapterNumberingOverride } from "../lib/chapterNumbering";
import { renameManuscriptItem } from "../lib/zwConfig/manuscript";
import type { MapPage } from "../lib/generated/zwMap";
import {
  isMapFileName,
  parseMapDocument,
  serializeMapDocument,
} from "../lib/mapDocument";
import { useTimelineMapsStore } from "./useTimelineMapsStore";
import { saveActive } from "../lib/tauri/core";
import { nextTick, ref, shallowRef, type Ref } from "vue";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  fromMaybe,
  toMaybe,
  unwrapMaybeOr,
  type Maybe,
} from "../lib/maybe";
import { defineStore } from "pinia";
import { ask } from "@tauri-apps/plugin-dialog";
import { trashLabel } from "../lib/deletedFiles";
import { displayFileName } from "../lib/fileNames";
import { isTauri } from "../lib/runtime";
import { pushProjectPath } from "../lib/projectRouter";
import {
  databaseResourcePath,
  manuscriptMapPath,
  manuscriptPathFromRelative,
  notesPath,
} from "../router/projectPaths";
import { isForeignJsonDocBody } from "../lib/zwdoc/format";
import {
  createProjectAct,
  createProjectChapter,
  createProjectScene,
  deleteProjectAct,
  deleteProjectChapter,
  deleteProjectScene,
  moveProjectScene,
  renameProjectAct,
  renameProjectChapter,
  reloadEditorFromDisk,
  renameProjectScene,
  switchProjectChapter,
  updateChapterSynopsis,
  updateChapterNumbering,
  updateSceneSynopsis,
} from "../lib/tauri/project";
import {
  createProjectDatabaseEntry,
  createProjectDatabaseMap,
  deleteProjectDatabaseEntry,
  openProjectDatabaseEntry,
  renameProjectDatabaseEntry,
} from "../lib/tauri/database";
import {
  createProjectNote,
  deleteProjectNote,
  openProjectNote,
  renameProjectNote,
} from "../lib/tauri/notes";
import {
  deleteParkedItem,
  openParkedItem,
  parkChapter,
  parkScene,
  renameParkedItem,
  unparkChapter,
  unparkScene,
} from "../lib/tauri/unplaced";
import { previewDeletedFile, restoreDeletedFile } from "../lib/tauri/git";
import { isLegacyDocumentPath } from "../lib/zwdoc/convertProject";
import { useProjectConversionStore } from "./useProjectConversionStore";
import type { EditorActiveTab, EditorPayload } from "../lib/tauri/core";
import {
  removeCollapsedActFolder,
  removeCollapsedChapterFolder,
  writeCollapsedActs,
  writeCollapsedChapters,
} from "../lib/localPrefs";
import {
  chapterFolderFromScenePath,
  findMapById,
  flattenChapters,
  sceneFileFromScenePath,
  PROJECT_CHAPTERS_DIR,
  type ManuscriptSlot,
  type ZwMapFigure,
} from "../lib/zwConfig";
import {
  reorderChaptersExactSlot,
  reorderScenesExactSlot,
} from "../lib/reorderExactSlot";
import { assertNever } from "../lib/assertNever";
import { dispatchInto } from "../lib/tea";
import { applyPayload } from "./editorPayload";
import { useEditorHeadingsStore } from "./useEditorHeadingsStore";
import { useEditorSaveStore } from "./useEditorSaveStore";
import { useEditorSessionStore } from "./useEditorSessionStore";
import { useOutlineActionsStore } from "./useOutlineActionsStore";
import { useProjectSettingsStore } from "./useProjectSettingsStore";
import type { EditorPortHandle } from "../editor/port";
import {
  updateWorkspace,
  type SceneMoveCollisionState,
  type WorkspaceModel,
  type WorkspaceMsg,
} from "./useProjectWorkspaceStore.update";

export type { SceneMoveCollisionState };

/**
 * Reorders `scenes` so `fromFile` ends up occupying the exact original slot
 * `toFile` held. Implemented by `reorderScenesExactSlot` in
 * `lib/reorderExactSlot` — re-exported here so existing imports keep working.
 */
export { reorderChaptersExactSlot, reorderScenesExactSlot };

type SceneRef = { folder: string; sceneFile: string };
/** `sceneFile: null` means "the chapter's own row" — append at the end.
 * Otherwise, drop always means "insert before this scene" — no invisible
 * before/after split (that existed briefly and was a real bug: nothing on
 * screen indicated which you'd get, so drops landed a slot off at random). */
type SceneDropTarget = {
  folder: string;
  sceneFile: Maybe<string>;
};

export const useProjectWorkspaceStore = defineStore("projectWorkspace", () => {
  const session = useEditorSessionStore();
  const { chapterLabel } = useChapterHeadings();
  const sceneOpenings = useSceneOpeningsStore();
  const outlineEntities = useOutlineEntitiesStore();
  const save = useEditorSaveStore();
  const headings = useEditorHeadingsStore();
  const settings = useProjectSettingsStore();
  const outlineActions = useOutlineActionsStore();

  const sceneMoveCollision = ref<Maybe<SceneMoveCollisionState>>(nothing);

  let onUnplacedChangedFn: Maybe<() => void> = nothing;

  function registerOnUnplacedChanged(fn: () => void): void {
    onUnplacedChangedFn = just(fn);
  }

  function notifyUnplacedChanged(): void {
    if (isJust(onUnplacedChangedFn)) onUnplacedChangedFn.value();
  }

  /** Set by `moveSceneAcrossChapters` Cmd; read by `performSceneMove`. */
  let crossChapterMoveResult: Maybe<boolean> = nothing;

  /** Manuscript is persisted by `writeManuscript` Cmds. Only the collision
   * slot is held here, and it must be written before Cmds run: a Cmd (cross-
   * chapter move) dispatches nested Msgs that open/clear the collision dialog,
   * and a post-Cmd write of the outer Msg's model would clobber them. */
  const workspaceModelHolder = {
    get value(): WorkspaceModel {
      return {
        manuscript: session.manuscript,
        sceneMoveCollision: sceneMoveCollision.value,
      };
    },
    set value(next: WorkspaceModel) {
      sceneMoveCollision.value = next.sceneMoveCollision;
    },
  };

  /** Run workspace update, then interpret Cmds (effects stay here). */
  async function dispatchManuscript(msg: WorkspaceMsg): Promise<void> {
    await dispatchInto(
      workspaceModelHolder,
      msg,
      updateWorkspace,
      async (cmd) => {
        const root = fromMaybe(session.rootPath);
        if (root === null && cmd.kind !== "writeManuscript") return;
        switch (cmd.kind) {
          case "writeManuscript":
            await settings.updateManuscript(cmd.manuscript);
            return;
          case "parkScene": {
            if (root === null) return;
            const scenePath = `${PROJECT_CHAPTERS_DIR}/${cmd.folder}/${cmd.sceneFile}`;
            const activeBasenameM = session.activeBasename;
            const parkingActive =
              session.editingSidecar === false &&
              isJust(activeBasenameM) &&
              activeBasenameM.value === scenePath;
            const result = await parkScene(root, cmd.folder, cmd.sceneFile);
            applyPayload(result);
            if (parkingActive) session.dirty = false;
            await headings.refreshEntityOccurrences();
            notifyUnplacedChanged();
            return;
          }
          case "parkChapter": {
            if (root === null) return;
            const chapterPrefix = `${PROJECT_CHAPTERS_DIR}/${cmd.folder}/`;
            const activeBasenameM = session.activeBasename;
            const parkingActive =
              isJust(activeBasenameM) &&
              activeBasenameM.value.startsWith(chapterPrefix);
            const result = await parkChapter(root, cmd.folder);
            applyPayload(result);
            if (parkingActive) session.dirty = false;
            await headings.refreshEntityOccurrences();
            notifyUnplacedChanged();
            return;
          }
          case "unparkScene": {
            if (root === null) return;
            try {
              const result = await unparkScene(
                root,
                cmd.name,
                cmd.toFolder,
                fromMaybe(cmd.toSceneFile),
              );
              applyPayload(result, { keepDoc: true });
              await headings.refreshEntityOccurrences();
              notifyUnplacedChanged();
            } catch (error) {
              console.error("Failed to unpark scene", error);
              if (isSceneCollisionError(error)) {
                window.alert(
                  `"${cmd.name}" can't be restored into "${cmd.toFolder}". A scene with that name already exists there. Rename the draft first, or restore it into a different chapter.`,
                );
                return;
              }
              window.alert(
                error instanceof Error ? error.message : String(error),
              );
            }
            return;
          }
          case "unparkChapter": {
            if (root === null) return;
            const result = await unparkChapter(root, cmd.name, cmd.atIndex);
            applyPayload(result, { keepDoc: true });
            await headings.refreshEntityOccurrences();
            notifyUnplacedChanged();
            return;
          }
          case "createNote": {
            if (root === null) return;
            await openSidecar("create note", () =>
              createProjectNote(root, cmd.name),
            );
            await focusEditorAfterCreate();
            return;
          }
          case "renameNote": {
            if (root === null) return;
            const result = await renameProjectNote(root, cmd.from, cmd.to);
            applyPayload(result, { keepDoc: true });
            if (
              isJust(session.notesLastFile) &&
              session.notesLastFile.value === cmd.from
            ) {
              session.notesLastFile = just(cmd.to);
            }
            return;
          }
          case "deleteNote": {
            if (root === null) return;
            const result = await deleteProjectNote(root, cmd.name);
            applyPayload(result);
            session.dirty = false;
            headings.entityOccurrences = [];
            if (
              isJust(session.notesLastFile) &&
              session.notesLastFile.value === cmd.name
            ) {
              session.notesLastFile = nothing;
            }
            return;
          }
          case "createDatabase": {
            if (root === null) return;
            let result =
              cmd.entryKind === "map"
                ? await createProjectDatabaseMap(root, cmd.name)
                : await createProjectDatabaseEntry(root, cmd.name);
            if (cmd.entryKind === "map" && isJust(cmd.mapPage)) {
              const map = parseMapDocument(result.content);
              map.page = { ...cmd.mapPage.value, fitOnOpen: true };
              result = await saveActive(serializeMapDocument(map), "");
            }
            applyPayload(result);
            if (cmd.entryKind === "map") {
              void useTimelineMapsStore().noteMapSaved(
                cmd.name,
                result.content,
              );
            }
            await headings.refreshEntityOccurrences();
            await pushProjectPath(databaseResourcePath(cmd.name));
            await focusEditorAfterCreate();
            return;
          }
          case "renameDatabase": {
            if (root === null) return;
            const result = await renameProjectDatabaseEntry(
              root,
              cmd.from,
              cmd.to,
            );
            applyPayload(result, { keepDoc: true });
            if (isMapFileName(cmd.from)) {
              void useTimelineMapsStore().noteMapRenamed(cmd.from, cmd.to);
            }
            await headings.refreshEntityOccurrences();
            if (
              isJust(session.databaseLastFile) &&
              session.databaseLastFile.value === cmd.from
            ) {
              session.databaseLastFile = just(cmd.to);
            }
            return;
          }
          case "deleteDatabase": {
            if (root === null) return;
            const result = await deleteProjectDatabaseEntry(root, cmd.name);
            applyPayload(result);
            session.dirty = false;
            headings.entityOccurrences = [];
            if (isMapFileName(cmd.name)) {
              void useTimelineMapsStore().noteMapRemoved(cmd.name);
            }
            if (
              isJust(session.databaseLastFile) &&
              session.databaseLastFile.value === cmd.name
            ) {
              session.databaseLastFile = nothing;
            }
            return;
          }
          case "createAct": {
            if (root === null) return;
            const result = await createProjectAct(
              root,
              cmd.name,
              fromMaybe(cmd.afterFolder),
            );
            applyPayload(result, { keepDoc: true });
            return;
          }
          case "renameAct":
          case "renameChapter": {
            if (root === null || isNothing(session.projectConfig)) return;
            const previous = session.manuscript
              .flatMap((item) =>
                item.type === "act"
                  ? [item, ...item.chapters]
                  : item.type === "chapter"
                    ? [item]
                    : [],
              )
              .find((item) => item.folder === cmd.fromFolder);
            if (typeof previous === "undefined") return;
            const setName = (name: string): void => {
              if (
                isNothing(session.projectConfig) ||
                isNothing(session.rootPath) ||
                session.rootPath.value !== root
              )
                return;
              session.projectConfig = just({
                ...session.projectConfig.value,
                manuscript: renameManuscriptItem(
                  session.manuscript,
                  cmd.fromFolder,
                  name,
                ),
              });
            };
            setName(cmd.toFolder.trim());
            try {
              const rename =
                cmd.kind === "renameAct"
                  ? renameProjectAct
                  : renameProjectChapter;
              await rename(root, cmd.fromFolder, cmd.toFolder);
            } catch (error) {
              setName(previous.name);
              throw error;
            }
            return;
          }
          case "deleteAct": {
            if (root === null) return;
            const result = await deleteProjectAct(root, cmd.folder);
            session.collapsedActs = removeCollapsedActFolder(root, cmd.folder);
            applyPayload(result, { keepDoc: true });
            return;
          }
          case "createChapterAt": {
            if (root === null) return;
            const previous = new Set(
              session.chapters.map((chapter) => chapter.folder),
            );
            const result = await createProjectChapter(root, cmd.name);
            applyPayload(result);
            const created = toMaybe(
              session.chapters.find(
                (chapter) => previous.has(chapter.folder) === false,
              ),
            );
            if (isJust(created)) {
              await dispatchManuscript({
                type: "MoveChapterToSlot",
                chapterFolder: created.value.folder,
                slot: { actFolder: cmd.actFolder, beforeFolder: nothing },
              });
            }
            await headings.refreshEntityOccurrences();
            return;
          }
          case "createChapter": {
            if (root === null) return;
            const result = await createProjectChapter(root, cmd.name);
            applyPayload(result);
            await headings.refreshEntityOccurrences();
            await focusEditorAfterCreate();
            return;
          }
          case "deleteChapter": {
            if (root === null) return;
            const result = await deleteProjectChapter(root, cmd.folder);
            session.collapsedChapters = removeCollapsedChapterFolder(
              root,
              cmd.folder,
            );
            applyPayload(result);
            session.dirty = false;
            await headings.refreshEntityOccurrences();
            return;
          }
          case "createScene": {
            if (root === null) return;
            const result = await createProjectScene(root, cmd.folder);
            applyPayload(result);
            await headings.refreshEntityOccurrences();
            await focusEditorAfterCreate();
            return;
          }
          case "renameScene": {
            if (root === null) return;
            const result = await renameProjectScene(
              root,
              cmd.folder,
              cmd.fromSceneFile,
              cmd.to,
            );
            applyPayload(result, { keepDoc: true });
            await headings.refreshEntityOccurrences();
            return;
          }
          case "deleteScene": {
            if (root === null) return;
            const scenePath = `${PROJECT_CHAPTERS_DIR}/${cmd.folder}/${cmd.sceneFile}`;
            const activeBasenameM = session.activeBasename;
            const deletingActive =
              session.editingSidecar === false &&
              isJust(activeBasenameM) &&
              activeBasenameM.value === scenePath;
            const result = await deleteProjectScene(
              root,
              cmd.folder,
              cmd.sceneFile,
            );
            applyPayload(result);
            if (deletingActive) session.dirty = false;
            await headings.refreshEntityOccurrences();
            return;
          }
          case "updateChapterSynopsis": {
            if (root === null) return;
            const result = await updateChapterSynopsis(
              root,
              cmd.folder,
              cmd.synopsis,
            );
            applyPayload(result, { keepDoc: true });
            return;
          }
          case "updateChapterNumbering": {
            if (root === null) return;
            const result = await updateChapterNumbering(
              root,
              cmd.folder,
              cmd.numbering,
            );
            applyPayload(result, { keepDoc: true });
            return;
          }
          case "updateSceneSynopsis": {
            if (root === null) return;
            const result = await updateSceneSynopsis(
              root,
              cmd.folder,
              cmd.sceneFile,
              cmd.synopsis,
            );
            applyPayload(result, { keepDoc: true });
            return;
          }
          case "moveSceneAcrossChapters": {
            if (root === null) {
              crossChapterMoveResult = just(false);
              return;
            }
            try {
              const renamedTo = fromMaybe(cmd.toSceneFile);
              const result = await moveProjectScene(
                root,
                cmd.from.folder,
                cmd.from.sceneFile,
                cmd.to.folder,
                renamedTo,
              );
              applyPayload(result, { keepDoc: true });
              await headings.refreshEntityOccurrences();

              if (
                isJust(cmd.to.sceneFile) &&
                result.kind === "project" &&
                isJust(result.project)
              ) {
                const movedFile = renamedTo ?? cmd.from.sceneFile;
                const chapter = toMaybe(
                  flattenChapters(result.project.value.manuscript).find(
                    (c) => c.folder === cmd.to.folder,
                  ),
                );
                if (isJust(chapter)) {
                  const nextScenes = reorderScenesExactSlot(
                    chapter.value.scenes,
                    movedFile,
                    cmd.to.sceneFile,
                  );
                  if (nextScenes !== chapter.value.scenes) {
                    await settings.updateChapterScenes(
                      cmd.to.folder,
                      nextScenes,
                    );
                  }
                }
              }
              await dispatchManuscript({ type: "ClearSceneMoveCollision" });
              crossChapterMoveResult = just(true);
            } catch (error) {
              if (isSceneCollisionError(error)) {
                const attemptedName =
                  fromMaybe(cmd.toSceneFile) ?? cmd.from.sceneFile;
                await dispatchManuscript({
                  type: "OpenSceneMoveCollision",
                  collision: {
                    from: cmd.from,
                    to: cmd.to,
                    attemptedName,
                    chapterName: cmd.to.folder,
                    error:
                      cmd.retrying === true
                        ? just(`“${attemptedName}” is already taken too.`)
                        : nothing,
                  },
                });
                crossChapterMoveResult = just(false);
                return;
              }
              console.error("Failed to move scene", error);
              window.alert(
                error instanceof Error ? error.message : String(error),
              );
              crossChapterMoveResult = just(false);
            }
            return;
          }
          case "restoreDeleted": {
            if (root === null) return;
            const restored = await restoreDeletedFile(
              root,
              cmd.relativePath,
              cmd.oid,
            );
            // A file deleted before the project converted comes back as
            // Markdown: convert it now (#259).
            const result = isLegacyDocumentPath(cmd.relativePath)
              ? (await useProjectConversionStore().convertIfNeeded(root))
                ? await reloadEditorFromDisk(root)
                : restored
              : restored;
            if (isJust(editor) && isJust(editor.value.value)) {
              editor.value.value.value.clearTrashPreview?.(false);
            }
            applyPayload(result);
            session.dirty = false;
            void headings.refreshEntityOccurrences();
            return;
          }
          case "previewDeleted": {
            if (root === null) return;
            const content = await previewDeletedFile(
              root,
              cmd.relativePath,
              cmd.oid,
            );
            const label = trashLabel(cmd.relativePath);
            const showTrashPreview =
              isJust(editor) && isJust(editor.value.value)
                ? toMaybe(editor.value.value.value.showTrashPreview)
                : nothing;
            if (isJust(showTrashPreview)) {
              showTrashPreview.value(content, label, cmd.relativePath);
            } else {
              session.trashPreview = just({
                label,
                path: cmd.relativePath,
                content,
              });
            }
            return;
          }
          case "renameParkedItem": {
            if (root === null) return;
            const result = await renameParkedItem(root, cmd.from, cmd.to);
            applyPayload(result, { keepDoc: true });
            if (
              isJust(session.notesLastFile) &&
              session.notesLastFile.value === cmd.from
            ) {
              session.notesLastFile = nothing;
            }
            return;
          }
          case "deleteParkedItem": {
            if (root === null) return;
            const result = await deleteParkedItem(root, cmd.name);
            applyPayload(result);
            session.dirty = false;
            headings.entityOccurrences = [];
            if (
              isJust(session.notesLastFile) &&
              session.notesLastFile.value === cmd.name
            ) {
              session.notesLastFile = nothing;
            }
            return;
          }
          case "selectManuscriptFile": {
            if (root === null) return;
            const path = unwrapMaybeOr(
              manuscriptPathFromRelative(cmd.name),
              "/",
            );
            await navigateThenMaybeLoad(
              path,
              cmd.skipNavigate === true
                ? just({ skipNavigate: true })
                : nothing,
              async () => {
                if (
                  session.editingSidecar === false &&
                  isJust(session.activeBasename) &&
                  session.activeBasename.value === cmd.name
                ) {
                  return;
                }
                const folder = chapterFolderFromScenePath(cmd.name);
                const sceneFile = sceneFileFromScenePath(cmd.name);
                if (isNothing(folder) || isNothing(sceneFile)) return;
                await saveIfDirty();
                try {
                  const result = await switchProjectChapter(
                    root,
                    folder.value,
                    sceneFile.value,
                  );
                  applyPayload(result, { navigationOnly: true });
                  session.manuscriptLastFile = just(cmd.name);
                  headings.entityOccurrences = [];
                } catch (error) {
                  console.error("Failed to switch file", error);
                }
              },
            );
            return;
          }
          case "selectManuscriptMap": {
            if (root === null || cmd.name.startsWith("Database/") === false)
              return;
            await navigateThenMaybeLoad(
              manuscriptMapPath(cmd.name),
              cmd.skipNavigate ? just({ skipNavigate: true }) : nothing,
              async () => {
                // Track the map in the backend too so disk/cloud reloads retain
                // its identity instead of restoring the previous prose buffer.
                await openSidecar("open manuscript map", () =>
                  openProjectDatabaseEntry(
                    root,
                    cmd.name.slice("Database/".length),
                  ),
                );
              },
            );
            return;
          }
          case "selectNote": {
            if (root === null) return;
            session.notesLastFile = just(cmd.name);
            await navigateThenMaybeLoad(
              notesPath(just(cmd.name)),
              cmd.skipNavigate === true
                ? just({ skipNavigate: true })
                : nothing,
              async () => {
                if (
                  isNothing(session.activeNote) ||
                  session.activeNote.value !== cmd.name
                ) {
                  await openSidecar("open note", () =>
                    openProjectNote(root, cmd.name),
                  );
                }
              },
            );
            return;
          }
          case "selectParkedItem": {
            if (root === null) return;
            session.notesLastFile = just(cmd.name);
            await navigateThenMaybeLoad(
              notesPath(just(cmd.name)),
              cmd.skipNavigate === true
                ? just({ skipNavigate: true })
                : nothing,
              async () => {
                if (
                  isNothing(session.activeNote) ||
                  session.activeNote.value !== cmd.name
                ) {
                  await openSidecar("open parked item", () =>
                    openParkedItem(root, cmd.name),
                  );
                }
              },
            );
            return;
          }
          case "selectDatabase": {
            if (root === null) return;
            session.databaseLastFile = just(cmd.name);
            await navigateThenMaybeLoad(
              databaseResourcePath(cmd.name),
              cmd.skipNavigate === true
                ? just({ skipNavigate: true })
                : nothing,
              async () => {
                if (
                  isNothing(session.activeDatabase) ||
                  session.activeDatabase.value !== cmd.name
                ) {
                  // Map heal can leave map JSON in `doc` while `activePath` is
                  // still a `.zwdoc`. Never flush that mismatch onto disk.
                  if (
                    session.activeMap === false &&
                    isForeignJsonDocBody(session.doc)
                  ) {
                    save.cancelAutosave();
                    session.dirty = false;
                  } else {
                    await saveIfDirty();
                  }
                  await runOrAlert(
                    "Failed to open database entry",
                    async () => {
                      const result = await openProjectDatabaseEntry(
                        root,
                        cmd.name,
                      );
                      applyPayload(result);
                      await headings.refreshEntityOccurrences();
                    },
                  );
                }
              },
            );
            return;
          }
          case "jumpInFile": {
            await dispatchManuscript({
              type: "SelectManuscriptFile",
              name: cmd.name,
              skipNavigate: true,
            });
            const path = unwrapMaybeOr(
              manuscriptPathFromRelative(cmd.name),
              "/",
            );
            await pushProjectPath(path);
            await nextTick();
            if (isJust(editor) && isJust(editor.value.value)) {
              editor.value.value.value.scrollToHeading?.(cmd.from);
            }
            return;
          }
          case "searchJump": {
            if (cmd.source === "chapter") {
              await dispatchManuscript({
                type: "JumpInFile",
                name: cmd.name,
                from: cmd.from,
              });
              return;
            }
            if (cmd.source === "note") {
              await dispatchManuscript({
                type: "SelectNote",
                name: cmd.name,
                skipNavigate: true,
              });
              await pushProjectPath(notesPath(just(cmd.name)));
            } else {
              await dispatchManuscript({
                type: "SelectDatabase",
                name: cmd.name,
                skipNavigate: true,
              });
              await pushProjectPath(databaseResourcePath(cmd.name));
            }
            await nextTick();
            if (isJust(editor) && isJust(editor.value.value)) {
              editor.value.value.value.scrollToHeading?.(cmd.from);
            }
            return;
          }
          default:
            assertNever(cmd);
        }
      },
    );
  }

  /** Tauri native confirm on desktop; `window.confirm` on web/demo. */
  async function confirmDestructive(
    message: string,
    title: string,
  ): Promise<boolean> {
    if (isTauri()) {
      return ask(message, { title, kind: "warning" });
    }
    return window.confirm(message);
  }

  let editor: Maybe<Ref<Maybe<EditorPortHandle>>> = nothing;
  function registerEditor(editorRef: Ref<Maybe<EditorPortHandle>>): void {
    editor = just(editorRef);
  }

  /** After creating a prose document, put the caret in the editor so the
   * author can type. Skip the storyboard (different surface; no editor). */
  async function focusEditorAfterCreate(): Promise<void> {
    if (session.storyboardView) return;
    // createDatabase remounts the routed view; wait for ProseDocEditor.
    await nextTick();
    await nextTick();
    if (isJust(editor) && isJust(editor.value.value)) {
      editor.value.value.value.focus?.();
    }
  }

  const navigateToTabFn =
    shallowRef<Maybe<(tab: EditorActiveTab) => void>>(nothing);
  function registerNavigateToTab(fn: (tab: EditorActiveTab) => void): void {
    navigateToTabFn.value = just(fn);
  }

  async function navigateThenMaybeLoad(
    path: string,
    opts: Maybe<{ skipNavigate?: boolean }>,
    load: () => Promise<void>,
  ): Promise<void> {
    const skipNavigate = isJust(opts) && opts.value.skipNavigate === true;
    if (skipNavigate === false) {
      const routerM = (await import("../lib/projectRouter")).getProjectRouter();
      if (isJust(routerM) && routerM.value.currentRoute.value.path !== path) {
        // Flush the leaving buffer before the route (and host) change. Otherwise
        // MapEditor can unmount while dirty, route sync opens the next file, and
        // a late autosave can write the old buffer onto the new active path.
        await saveIfDirty();
        await pushProjectPath(path);
        return;
      }
    }
    await load();
  }

  async function saveIfDirty(): Promise<void> {
    if (session.dirty) await save.flushSave();
  }

  /** Runs `fn`, logging and alerting on failure with a consistent shape. Not a
   * fit for every catch site here — skip it where there's extra branching
   * (e.g. a collision needs its own recovery UI instead of a plain alert) or
   * a `finally` block managing other state. */
  async function runOrAlert(
    label: string,
    fn: () => Promise<void>,
  ): Promise<void> {
    try {
      await fn();
    } catch (error) {
      console.error(label, error);
      window.alert(error instanceof Error ? error.message : String(error));
    }
  }

  /** Serializes every file-identity-changing operation (switch/create/rename/
   * delete/park/unpark/jump) onto a single queue — defense in depth on top of
   * removing the "outgoing content" pattern (see the editor-rearchitecture
   * plan). Two switches firing close together (e.g. a stray unawaited
   * `navigateToTab` racing the router guard's own re-resolution) can now never
   * interleave: each waits for the previous one to fully finish — including
   * its own `saveIfDirty()` — before starting. Wraps only the *exported*
   * function references below, never the local ones — an internal call (e.g.
   * `onJumpInFile` calling `onSelectProjectFile`) must reach the raw function
   * directly, or it would enqueue behind itself and deadlock. */
  let pending: Promise<void> = Promise.resolve();
  function serialize<A extends unknown[], T>(
    fn: (...args: A) => Promise<T>,
  ): (...args: A) => Promise<T> {
    return (...args: A) => {
      const run = pending.then(() => fn(...args));
      pending = run.then(
        () => void 0,
        () => void 0,
      );
      return run;
    };
  }

  async function onSetCollapsedChapters(collapsed: string[]): Promise<void> {
    const root = fromMaybe(session.rootPath);
    if (root === null) return;
    session.collapsedChapters = collapsed;
    writeCollapsedChapters(root, collapsed);
  }

  /** Acts collapse independently of chapters, and both the outline and the
   * Storyboard read the same localStorage list so a fold follows you between
   * the two views. */
  async function onToggleActCollapsed(actPath: string): Promise<void> {
    const root = fromMaybe(session.rootPath);
    if (root === null) return;
    const collapsed = session.collapsedActs.includes(actPath)
      ? session.collapsedActs.filter((p) => p !== actPath)
      : [...session.collapsedActs, actPath];
    session.collapsedActs = collapsed;
    writeCollapsedActs(root, collapsed);
  }

  /** Creates an act after the span containing `afterFolder`, defaulting to the
   * chapter you're currently writing in. Acts hold no prose, so unlike a new
   * chapter this deliberately leaves the editor where it is. */
  async function onCreateProjectAct(
    name: string,
    afterFolder: Maybe<string> = nothing,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const afterFromActive = isJust(session.activeBasename)
      ? chapterFolderFromScenePath(session.activeBasename.value)
      : nothing;
    const after: Maybe<string> = isJust(afterFolder)
      ? afterFolder
      : afterFromActive;
    await runOrAlert("Failed to create act", async () => {
      await dispatchManuscript({
        type: "CreateAct",
        name,
        afterFolder: after,
      });
    });
  }

  async function onRenameProjectAct(
    fromFolder: string,
    toFolder: string,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await runOrAlert("Failed to Rename", async () => {
      await dispatchManuscript({
        type: "RenameAct",
        fromFolder,
        toFolder,
      });
    });
  }

  /** Deleting an act removes only the grouping. Its chapters are promoted to the
   * top level in place, so no prose is ever lost to this action. */
  async function onDeleteProjectAct(folder: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await runOrAlert("Failed to delete act", async () => {
      await dispatchManuscript({ type: "DeleteAct", folder });
    });
  }

  async function openSidecar(
    label: string,
    open: () => Promise<EditorPayload>,
  ): Promise<void> {
    await saveIfDirty();
    await runOrAlert(`Failed to ${label}`, async () => {
      const result = await open();
      applyPayload(result);
      headings.entityOccurrences = [];
    });
  }

  async function onSelectProjectFile(
    name: string,
    opts?: { skipNavigate?: boolean },
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await dispatchManuscript({
      type: "SelectManuscriptFile",
      name,
      skipNavigate: opts?.skipNavigate === true,
    });
  }

  /** Open a manuscript map leaf preview (`/map/<encoded mapPath>`). */
  async function onSelectManuscriptMap(
    mapPath: string,
    opts?: { skipNavigate?: boolean },
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await dispatchManuscript({
      type: "SelectManuscriptMap",
      name: mapPath.replace(/\\/g, "/").replace(/^\//, ""),
      skipNavigate: opts?.skipNavigate === true,
    });
  }

  async function onSelectProjectNote(
    name: string,
    opts?: { skipNavigate?: boolean },
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await dispatchManuscript({
      type: "SelectNote",
      name,
      skipNavigate: opts?.skipNavigate === true,
    });
  }

  async function onSelectParkedItem(
    name: string,
    opts?: { skipNavigate?: boolean },
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await dispatchManuscript({
      type: "SelectParkedItem",
      name,
      skipNavigate: opts?.skipNavigate === true,
    });
  }

  async function onRenameParkedItem(from: string, to: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await saveIfDirty();
    await runOrAlert("Failed to rename parked item", async () => {
      // Backend normalizes `to` differently for scene vs chapter; drop the
      // last-file pointer when it goes stale (same as delete).
      await dispatchManuscript({ type: "RenameParkedItem", from, to });
    });
  }

  function noteDisplayName(name: string): string {
    const entry = outlineEntities.notes.find((item) => item.name === name);
    if (typeof entry !== "undefined") return noteEntryDisplayName(entry);
    const parked = outlineEntities.unplaced.find((item) => item.name === name);
    return typeof parked !== "undefined"
      ? unplacedItemDisplayName(parked)
      : displayFileName(name);
  }

  async function onDeleteParkedItem(
    name: string,
    label: string,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const confirmed = await confirmDestructive(
      `Delete ${label} “${noteDisplayName(name)}”? You can restore it later from Trash.`,
      `Delete ${label}`,
    );
    if (confirmed === false) return;
    save.cancelAutosave();
    if (session.dirty) {
      await save.flushSave();
    }
    await runOrAlert("Failed to delete parked item", async () => {
      await dispatchManuscript({ type: "DeleteParkedItem", name });
    });
  }

  async function onNewProjectNote(name: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await dispatchManuscript({ type: "NewNote", name });
  }

  async function onRenameProjectNote(from: string, to: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await saveIfDirty();
    await runOrAlert("Failed to rename note", async () => {
      await dispatchManuscript({ type: "RenameNote", from, to });
    });
  }

  async function onDeleteProjectNote(name: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const confirmed = await confirmDestructive(
      `Delete note “${noteDisplayName(name)}”? You can restore it later from Trash.`,
      "Delete note",
    );
    if (confirmed === false) return;
    save.cancelAutosave();
    if (session.dirty) {
      await save.flushSave();
    }
    await runOrAlert("Failed to delete note", async () => {
      await dispatchManuscript({ type: "DeleteNote", name });
    });
  }

  async function onSelectProjectDatabase(
    name: string,
    opts?: { skipNavigate?: boolean },
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await dispatchManuscript({
      type: "SelectDatabase",
      name,
      skipNavigate: opts?.skipNavigate === true,
    });
  }

  async function onNewProjectDatabase(
    name: string,
    options: { kind: "entry" | "map"; mapPage: Maybe<MapPage> },
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await saveIfDirty();
    await runOrAlert("Failed to create database entry", async () => {
      await dispatchManuscript({
        type: "NewDatabase",
        name,
        entryKind: options.kind,
        mapPage: options.mapPage,
      });
    });
  }

  async function onRenameProjectDatabase(
    from: string,
    to: string,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await saveIfDirty();
    await runOrAlert("Failed to rename database entry", async () => {
      await dispatchManuscript({ type: "RenameDatabase", from, to });
    });
  }

  async function onDeleteProjectDatabase(name: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const confirmed = await confirmDestructive(
      `Delete worldbuilding entry “${displayFileName(name)}”? You can restore it later from Trash.`,
      "Delete worldbuilding entry",
    );
    if (confirmed === false) return;
    save.cancelAutosave();
    if (session.dirty) {
      await save.flushSave();
    }
    await runOrAlert("Failed to delete database entry", async () => {
      await dispatchManuscript({ type: "DeleteDatabase", name });
    });
  }

  async function onJumpInFile(name: string, from: number): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await dispatchManuscript({ type: "JumpInFile", name, from });
  }

  async function onSearchJump(
    source: "chapter" | "note" | "database",
    name: string,
    from: number,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await dispatchManuscript({ type: "SearchJump", source, name, from });
  }

  async function onNewStoryboardChapter(
    name: string,
    actFolder: Maybe<string>,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await saveIfDirty();
    await runOrAlert("Failed to create chapter", () =>
      dispatchManuscript({ type: "NewChapterAt", name, actFolder }),
    );
  }

  async function onNewStoryboardAct(name: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await runOrAlert("Failed to create act", () =>
      dispatchManuscript({ type: "CreateAct", name, afterFolder: nothing }),
    );
  }

  async function onNewProjectFile(name: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await saveIfDirty();
    await runOrAlert("Failed to create file", async () => {
      await dispatchManuscript({ type: "NewChapter", name });
    });
  }

  async function onRenameProjectFile(from: string, to: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const fromFolderM = chapterFolderFromScenePath(from);
    if (isNothing(fromFolderM)) return;
    await saveIfDirty();
    await runOrAlert("Failed to rename file", async () => {
      await dispatchManuscript({
        type: "RenameChapter",
        fromFolder: fromFolderM.value,
        toFolder: to,
      });
    });
  }

  async function onUpdateChapterSynopsis(
    folder: string,
    synopsis: string,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await runOrAlert("Failed to update chapter synopsis", async () => {
      await dispatchManuscript({
        type: "UpdateChapterSynopsis",
        folder,
        synopsis,
      });
    });
  }

  async function onUpdateChapterNumbering(
    folder: string,
    numbering: ChapterNumberingOverride,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await runOrAlert("Failed to update chapter numbering", async () => {
      await dispatchManuscript({
        type: "UpdateChapterNumbering",
        folder,
        numbering,
      });
    });
  }

  async function onUpdateSceneSynopsis(
    folder: string,
    sceneFile: string,
    synopsis: string,
  ): Promise<void> {
    // The active scene's frontmatter is owned by session.synopsis + the
    // [doc, synopsis] autosave watcher in useEditorSave.ts. Routing it through
    // update_scene_synopsis too would mean two writers on one file: save_active
    // renders frontmatter from the (now stale) session.synopsis and would
    // clobber whatever this just wrote.
    const active = session.activeSceneInfo;
    if (
      isJust(active) &&
      active.value.folder === folder &&
      active.value.sceneFile === sceneFile
    ) {
      session.synopsis = synopsis;
      // The board reads config directly while the active editor owns the save.
      const scene = toMaybe(
        active.value.chapter.scenes.find((item) => item.file === sceneFile),
      );
      if (isJust(scene)) scene.value.synopsis = synopsis;
      return;
    }
    if (isNothing(session.rootPath)) return;
    await runOrAlert("Failed to update scene synopsis", async () => {
      await dispatchManuscript({
        type: "UpdateSceneSynopsis",
        folder,
        sceneFile,
        synopsis,
      });
    });
  }

  async function onDeleteProjectFile(name: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const folderM = chapterFolderFromScenePath(name);
    if (isNothing(folderM)) return;
    const folder = folderM.value;

    const confirmed = await confirmDestructive(
      `Delete chapter “${chapterLabel(folder)}”? You can restore it later from Trash.`,
      "Delete chapter",
    );
    if (confirmed === false) return;

    save.cancelAutosave();
    if (session.dirty) {
      await save.flushSave();
      save.cancelAutosave();
    }

    await runOrAlert("Failed to delete file", async () => {
      await dispatchManuscript({ type: "DeleteChapter", folder });
    });
  }

  async function onNewProjectScene(folder: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await saveIfDirty();
    await runOrAlert("Failed to create scene", async () => {
      await dispatchManuscript({ type: "NewScene", folder });
    });
  }

  /** Deletes a scene without the user-facing confirmation. For cleanup steps
   * (e.g. the auto-created placeholder scene left behind by a drag-to-new-chapter
   * move) where the user never saw the file and shouldn't be asked about it. */
  async function deleteSceneNow(
    folder: string,
    sceneFile: string,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    save.cancelAutosave();
    if (session.dirty) {
      await save.flushSave();
      save.cancelAutosave();
    }

    await runOrAlert("Failed to delete scene", async () => {
      await dispatchManuscript({ type: "DeleteScene", folder, sceneFile });
    });
  }

  async function onDeleteProjectScene(
    folder: string,
    sceneFile: string,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const scene = session.chapters
      .find((chapter) => chapter.folder === folder)
      ?.scenes.find((item) => item.file === sceneFile);
    const sceneName =
      typeof scene === "undefined"
        ? "Untitled scene"
        : matchMaybe(sceneCustomName(scene), {
            just: (name) => name,
            nothing: () =>
              sceneOpeningText(
                sceneOpenings.openingFor(`Chapters/${folder}/${sceneFile}`),
                scene.synopsis,
              ),
          });
    const confirmed = await confirmDestructive(
      `Delete scene “${sceneName}” from “${chapterLabel(folder)}”? You can restore it later from Trash.`,
      "Delete scene",
    );
    if (confirmed === false) return;
    await deleteSceneNow(folder, sceneFile);
  }

  /** Same "flush whatever else is dirty first" shape as `deleteSceneNow`. */
  async function onParkScene(folder: string, sceneFile: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    save.cancelAutosave();
    if (session.dirty) {
      await save.flushSave();
      save.cancelAutosave();
    }
    await runOrAlert("Failed to park scene", async () => {
      await dispatchManuscript({ type: "ParkScene", folder, sceneFile });
    });
  }

  async function onParkChapter(folder: string): Promise<void> {
    if (isNothing(session.rootPath)) return;
    save.cancelAutosave();
    if (session.dirty) {
      await save.flushSave();
      save.cancelAutosave();
    }
    await runOrAlert("Failed to park chapter", async () => {
      await dispatchManuscript({ type: "ParkChapter", folder });
    });
  }

  async function onUnparkSceneIntoChapter(
    name: string,
    toFolder: string,
    toSceneFile: Maybe<string>,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    // Collision alert lives in the unparkScene Cmd runner (not sceneMoveCollision).
    await dispatchManuscript({
      type: "UnparkSceneIntoChapter",
      name,
      toFolder,
      toSceneFile,
    });
  }

  async function onUnparkChapter(name: string, atIndex: number): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await runOrAlert("Failed to unpark chapter", async () => {
      await dispatchManuscript({ type: "UnparkChapter", name, atIndex });
    });
  }

  async function onRenameProjectScene(
    folder: string,
    fromSceneFile: string,
    to: string,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await saveIfDirty();
    await runOrAlert("Failed to rename scene", async () => {
      await dispatchManuscript({
        type: "RenameScene",
        folder,
        fromSceneFile,
        to,
      });
    });
  }

  const TEMP_PLACEHOLDER_SCENE = "zw-temp-placeholder-scene.zwdoc";

  /** Machine-checkable prefix the backend puts on this specific error (see
   * `SCENE_NAME_COLLISION_PREFIX` in commands_project.rs) — checking the
   * prefix rather than matching on the human-readable text means a copy edit
   * to that text can't silently break this detection. */
  const SCENE_NAME_COLLISION_PREFIX = "SCENE_NAME_COLLISION: ";

  /** Backend surfaces this collision case with a distinct, matchable message
   * (see `move_project_scene` in commands_project.rs) so it can be resolved
   * with a rename prompt instead of a dead-end alert. */
  function isSceneCollisionError(error: unknown): boolean {
    return (
      error instanceof Error &&
      error.message.startsWith(SCENE_NAME_COLLISION_PREFIX)
    );
  }

  /** Cross-chapter scene move via Msg → `moveSceneAcrossChapters` Cmd.
   * Backend always appends; the Cmd re-splices with `reorderScenesExactSlot`
   * when `to.sceneFile` names a row. */
  async function performSceneMove(
    from: SceneRef,
    to: SceneDropTarget,
    opts: { toSceneFile?: string; retrying?: boolean } = {},
  ): Promise<boolean> {
    if (isNothing(session.rootPath)) return false;
    crossChapterMoveResult = nothing;
    await dispatchManuscript({
      type: "MoveSceneAcrossChapters",
      from,
      to,
      toSceneFile: toMaybe(opts.toSceneFile),
      retrying: opts.retrying === true,
    });
    return matchMaybe(crossChapterMoveResult, {
      just: (ok) => ok === true,
      nothing: () => false,
    });
  }

  async function onReorderProjectScenes(
    from: SceneRef,
    to: SceneDropTarget,
  ): Promise<void> {
    const root = fromMaybe(session.rootPath);
    if (root === null) return;

    // #237 spike: same-chapter reorder goes through Msg → update → Cmd.
    if (from.folder === to.folder) {
      await dispatchManuscript({
        type: "ReorderScenesSameChapter",
        folder: from.folder,
        fromFile: from.sceneFile,
        toFile: to.sceneFile,
      });
      return;
    }

    const fromChapter = toMaybe(
      session.chapters.find((c) => c.folder === from.folder),
    );
    if (isNothing(fromChapter)) return;
    if (fromChapter.value.scenes.length <= 1) {
      window.alert("A chapter must have at least one scene.");
      return;
    }
    await performSceneMove(from, to);
  }

  async function onResolveSceneMoveCollision(name: string): Promise<void> {
    const pendingCollision = sceneMoveCollision.value;
    if (isNothing(pendingCollision)) return;
    await performSceneMove(
      pendingCollision.value.from,
      pendingCollision.value.to,
      {
        toSceneFile: name,
        retrying: true,
      },
    );
  }

  function onCancelSceneMoveCollision(): void {
    void dispatchManuscript({ type: "ClearSceneMoveCollision" });
  }

  /** Drag a scene onto the "new chapter" drop zone: the chapter (with its
   * auto-created placeholder scene) already exists by the time this runs —
   * `onNewFile()`'s usual dialog flow created it. Rename the placeholder out
   * of the way first so it can't collide with a dragged scene that's also still
   * on its default name (the common case), then move the real scene in, then
   * clean up the placeholder — never below 1 scene, so the invariant always holds. */
  async function onMoveSceneIntoNewChapter(
    from: SceneRef,
    newFolder: string,
  ): Promise<void> {
    const root = fromMaybe(session.rootPath);
    if (root === null) return;
    const placeholderFile = toMaybe(
      session.chapters.find((c) => c.folder === newFolder)?.scenes[0]?.file,
    );
    if (isNothing(placeholderFile)) return;
    await onRenameProjectScene(
      newFolder,
      placeholderFile.value,
      TEMP_PLACEHOLDER_SCENE,
    );
    const moved = await performSceneMove(from, {
      folder: newFolder,
      sceneFile: nothing,
    });
    if (moved === false) return;
    await deleteSceneNow(newFolder, TEMP_PLACEHOLDER_SCENE);
  }

  async function onRestoreDeleted(
    relativePath: string,
    oid: string,
  ): Promise<void> {
    if (isJust(session.trashRestoringPath)) return;
    session.trashRestoringPath = just(relativePath);
    try {
      if (isNothing(session.rootPath)) return;
      save.cancelAutosave();
      if (session.dirty) {
        await save.flushSave();
        save.cancelAutosave();
      }
      await dispatchManuscript({
        type: "RestoreDeleted",
        relativePath,
        oid,
      });
    } catch (error) {
      console.error("Failed to restore deleted file", error);
      window.alert(error instanceof Error ? error.message : String(error));
    } finally {
      session.trashRestoringPath = nothing;
      if (isJust(outlineActions.refreshTrash)) {
        outlineActions.refreshTrash.value();
      }
    }
  }

  async function onPreviewDeleted(
    relativePath: string,
    oid: string,
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await runOrAlert("Failed to preview deleted file", async () => {
      await dispatchManuscript({
        type: "PreviewDeleted",
        relativePath,
        oid,
      });
    });
  }

  function onClearTrashPreview(): void {
    if (isJust(editor) && isJust(editor.value.value)) {
      editor.value.value.value.clearTrashPreview?.();
    }
    session.trashPreview = nothing;
  }

  function onTrashPreviewChange(
    preview: Maybe<{ label: string; path: string; content: string }>,
  ): void {
    session.trashPreview = preview;
  }

  /** Commits a chapter drag: `slot` says which list it lands in (an act, or the
   * ungrouped top level) and which sibling it lands before.
   * #237 spike: Msg → pure update → writeManuscript Cmd. */
  async function onMoveChapterToSlot(
    chapterFolder: string,
    slot: ManuscriptSlot,
  ): Promise<void> {
    await dispatchManuscript({
      type: "MoveChapterToSlot",
      chapterFolder,
      slot,
    });
  }

  /** Commits an act drag. The act's chapters ride along, so this only ever
   * reorders top-level items.
   * #237 spike: Msg → pure update → writeManuscript Cmd. */
  async function onMoveAct(
    actFolder: string,
    beforeFolder: Maybe<string>,
  ): Promise<void> {
    await dispatchManuscript({
      type: "MoveAct",
      actFolder,
      beforeFolder,
    });
  }

  async function onIncludeMapInManuscript(
    figure: ZwMapFigure,
    atIndex?: number,
  ): Promise<void> {
    await dispatchManuscript({
      type: "IncludeMap",
      figure,
      atIndex:
        typeof atIndex === "number" ? atIndex : session.manuscript.length,
    });
  }

  async function onRemoveMapFromManuscript(id: string): Promise<void> {
    const figureM = findMapById(session.manuscript, id);
    const title = isJust(figureM) ? figureM.value.title : "this map";
    const confirmed = await confirmDestructive(
      `Remove “${title}” from the book? The map file stays in Maps.`,
      "Remove from book",
    );
    if (confirmed === false) return;
    await dispatchManuscript({ type: "RemoveMap", id });
  }

  async function onMoveMap(
    id: string,
    beforeKey: Maybe<string>,
  ): Promise<void> {
    await dispatchManuscript({ type: "MoveMap", id, beforeKey });
  }

  async function onUpdateMapFigure(
    id: string,
    figure: ZwMapFigure,
  ): Promise<void> {
    await dispatchManuscript({ type: "UpdateMapFigure", id, figure });
  }

  return {
    sceneMoveCollision,
    registerEditor,
    registerNavigateToTab,
    registerOnUnplacedChanged,
    onSelectProjectFile: serialize(onSelectProjectFile),
    onSelectManuscriptMap: serialize(onSelectManuscriptMap),
    onSelectProjectNote: serialize(onSelectProjectNote),
    onNewProjectNote: serialize(onNewProjectNote),
    onRenameProjectNote: serialize(onRenameProjectNote),
    onDeleteProjectNote: serialize(onDeleteProjectNote),
    onSelectParkedItem: serialize(onSelectParkedItem),
    onRenameParkedItem: serialize(onRenameParkedItem),
    onDeleteParkedItem: serialize(onDeleteParkedItem),
    onSelectProjectDatabase: serialize(onSelectProjectDatabase),
    onNewProjectDatabase: serialize(onNewProjectDatabase),
    onRenameProjectDatabase: serialize(onRenameProjectDatabase),
    onDeleteProjectDatabase: serialize(onDeleteProjectDatabase),
    onJumpInFile: serialize(onJumpInFile),
    onSearchJump: serialize(onSearchJump),
    onNewProjectFile: serialize(onNewProjectFile),
    onRenameProjectFile: serialize(onRenameProjectFile),
    onDeleteProjectFile: serialize(onDeleteProjectFile),
    onUpdateChapterSynopsis,
    onUpdateChapterNumbering: serialize(onUpdateChapterNumbering),
    onUpdateSceneSynopsis,
    onSetCollapsedChapters,
    onNewProjectScene: serialize(onNewProjectScene),
    onRenameProjectScene: serialize(onRenameProjectScene),
    onDeleteProjectScene: serialize(onDeleteProjectScene),
    onReorderProjectScenes: serialize(onReorderProjectScenes),
    onMoveSceneIntoNewChapter: serialize(onMoveSceneIntoNewChapter),
    onResolveSceneMoveCollision: serialize(onResolveSceneMoveCollision),
    onCancelSceneMoveCollision,
    onRestoreDeleted: serialize(onRestoreDeleted),
    onPreviewDeleted,
    onClearTrashPreview,
    onTrashPreviewChange,
    onMoveChapterToSlot,
    onNewStoryboardChapter: serialize(onNewStoryboardChapter),
    onNewStoryboardAct: serialize(onNewStoryboardAct),
    onMoveAct,
    onIncludeMapInManuscript,
    onRemoveMapFromManuscript,
    onMoveMap,
    onUpdateMapFigure,
    onCreateProjectAct: serialize(onCreateProjectAct),
    onRenameProjectAct: serialize(onRenameProjectAct),
    onDeleteProjectAct: serialize(onDeleteProjectAct),
    onToggleActCollapsed,
    onParkScene: serialize(onParkScene),
    onParkChapter: serialize(onParkChapter),
    onUnparkSceneIntoChapter: serialize(onUnparkSceneIntoChapter),
    onUnparkChapter: serialize(onUnparkChapter),
  };
});
